/*
 Lightweight performance instrumentation utilities for local diagnostics.
 - No external deps. Safe to import in client code.
 - Observes layout shifts (CLS approximation), long tasks, and optional FPS sampling.
 - Provides simple mark/measure helpers for conversation switching and message pane paint.
*/

 

type PerfConfig = {
  observeCLS?: boolean;
  observeLongTasks?: boolean;
  observeFPS?: boolean;
};

type PerfWindow = Window & {
  __voleyPerf?: {
    cls: number;
    longTasks: Array<{ start: number; duration: number }>;
    fpsSamples: number[];
    marks: Record<string, number>;
  };
};

const perfWindow = (typeof window !== 'undefined' ? window : undefined) as PerfWindow | undefined;

function ensureStore() {
  if (!perfWindow) return;
  if (!perfWindow.__voleyPerf) {
    perfWindow.__voleyPerf = { cls: 0, longTasks: [], fpsSamples: [], marks: {} };
  }
}

export function initPerfObservers(config: PerfConfig = {}) {
  if (!perfWindow) return;
  ensureStore();

  try {
    if (config.observeCLS && 'PerformanceObserver' in perfWindow) {
      const po = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries() as PerformanceEntry[]) {
          const e = entry as unknown as { hadRecentInput?: boolean; value?: number };
          if (entry.entryType === 'layout-shift' && !e.hadRecentInput) {
            if (perfWindow?.__voleyPerf) perfWindow.__voleyPerf.cls += e.value || 0;
          }
        }
      });
      try {
        po.observe({ type: 'layout-shift', buffered: true } as PerformanceObserverInit);
      } catch {}
    }
  } catch {}

  try {
    if (config.observeLongTasks && 'PerformanceObserver' in perfWindow) {
      const po = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (entry.entryType === 'longtask') {
            if (perfWindow?.__voleyPerf) perfWindow.__voleyPerf.longTasks.push({ start: entry.startTime, duration: entry.duration });
          }
        }
      });
      try {
        po.observe({ type: 'longtask', buffered: true } as PerformanceObserverInit);
      } catch {}
    }
  } catch {}

  if (config.observeFPS) {
    // Rough FPS sampling
    const last = performance.now();
    let frames = 0;
    let bucketStart = last;
    const loop = () => {
      const now = performance.now();
      frames += 1;
      if (now - bucketStart >= 1000) {
        if (perfWindow?.__voleyPerf) perfWindow.__voleyPerf.fpsSamples.push(frames);
        frames = 0;
        bucketStart = now;
      }
      if (perfWindow) {
        perfWindow.requestAnimationFrame(loop);
      }
    };
    perfWindow.requestAnimationFrame(loop);
  }
}

export function mark(name: string) {
  if (!perfWindow) return;
  ensureStore();
  try {
    performance.mark(name);
  } catch {}
  if (perfWindow?.__voleyPerf) perfWindow.__voleyPerf.marks[name] = performance.now();
}

export function measure(name: string, startMark: string, endMark?: string) {
  if (!perfWindow) return 0;
  try {
    if (endMark) performance.mark(endMark);
    performance.measure(name, startMark, endMark);
    const entries = performance.getEntriesByName(name);
    const last = entries[entries.length - 1];
    return last?.duration || 0;
  } catch {
    const start = perfWindow.__voleyPerf?.marks[startMark] || 0;
    const end = performance.now();
    return Math.max(0, end - start);
  }
}

// High-level helpers
export function markThreadClick(conversationId: string | undefined) {
  if (!conversationId) return;
  mark(`voley:thread-click:${conversationId}`);
}

export function markMessagePaneRenderStart(conversationId?: string) {
  mark(`voley:message-pane:start:${conversationId ?? 'unknown'}`);
}

export function markMessagePaneFirstPaint(conversationId?: string) {
  // rAF to align with next paint
  requestAnimationFrame(() => {
    const start = `voley:message-pane:start:${conversationId ?? 'unknown'}`;
    const end = `voley:message-pane:first-paint:${conversationId ?? 'unknown'}`;
    measure(`voley:message-pane:ttfp:${conversationId ?? 'unknown'}`, start, end);

    // If we have a thread-click mark, also report click -> first paint
    try {
      const clickMark = `voley:thread-click:${conversationId ?? 'unknown'}`;
      measure(`voley:thread-click:to-first-paint:${conversationId ?? 'unknown'}`, clickMark, end);
    } catch {}
  });
}

export function getPerfSnapshot() {
  if (!perfWindow) return null;
  ensureStore();
  return { ...perfWindow.__voleyPerf };
}


